import { useEffect, useState } from 'react'
import { usePoints } from '@/src/common/hooks/usePoints'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { router } from 'expo-router'
import { Button } from '@/src/common/components/Button'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { formatCurrency } from '@/src/common/utils/general'
import { DesignSystem } from '@/src/design-system'
import { CashForMortgageStore } from '../statsig/store'
import { randomUUID } from '@/src/common/utils/randomUUID'
import { usePointsToCashForMortgageDynamicConfig } from '@/src/experiences/points-to-cash-for-mortgage/statsig/dynamic-config/usePointsToCashForMortgageDynamicConfig'
import { mesaColorNames, typography } from '@/src/common/theme'
import { PointsToCashForMortgageGql } from '../gql'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { View, Text } from 'react-native'

function PointsToCashForMortgage() {
  const Alert = useAlert(PointsToCashForMortgage.name)

  const { dynamicConfig } = usePointsToCashForMortgageDynamicConfig()
  const centsPerPointConversion =
    dynamicConfig?.value.MESA_MORTGAGE_TRANSFER_POINT_TO_CENTS_CONVERSION ?? 0.8
  const minimumDollars = dynamicConfig?.value.MESA_MORTGAGE_TRANSFER_MINIMUM_DOLLAR ?? 20
  const maxDollarsPerMonth =
    dynamicConfig?.value.MESA_MORTGAGE_TRANSFER_MAXIMUM_MONTHLY_DOLLAR ?? 200
  const dollarsPerPointConversion = centsPerPointConversion / 100
  const maxPeriodDays = dynamicConfig?.value.MESA_MORTGAGE_TRANSFER_MAXIMUM_PERIOD_DAYS ?? 30

  const pointsBalance = usePoints().query.data?.getPointsBalance.active.value || 0

  const { data, loading: loadingMetrics } = useLoggedInQuery(
    PointsToCashForMortgageGql.Queries.GET_MORTGAGE_REDEMPTION_METRICS,
    { fetchPolicy: 'cache-and-network' },
  )

  const {
    setPointsToRedeem,
    setCreditAmount,
    setConversionRate,
    setRemainingPoints,
    setIdempotencyKey,
    clear,
  } = CashForMortgageStore.useCashForMortgage()

  const [cashForMortgageAmount, setCashForMortgageAmount] = useState<number>(0)
  const points = Math.floor(cashForMortgageAmount / dollarsPerPointConversion)

  const isEligible = data?.getCashForMortgageRedemptionMetrics.isEligible
  const isRemainingMonthlyCentsEligible =
    data?.getCashForMortgageRedemptionMetrics.remainingMonthlyCents &&
    data.getCashForMortgageRedemptionMetrics.remainingMonthlyCents > minimumDollars * 100

  const handleContinue = () => {
    // check user has more points than they're trying to redeem
    if (points > pointsBalance) {
      Alert.alert(
        'Insufficient Mesa Points',
        'You do not have the required number of Mesa Points for the requested Credit amount at this time. Please select a different Credit amount in order to proceed with redemption.',
      )
      return
    }

    if (cashForMortgageAmount < minimumDollars) {
      Alert.alert(
        'Invalid Redemption Amount',
        `The minimum redemption amount is ${formatCurrency(String(minimumDollars), {
          isFloat: false,
          decimals: null,
        })}.`,
      )
      return
    }

    if (cashForMortgageAmount > maxDollarsPerMonth) {
      Alert.alert(
        'Invalid Redemption Amount',
        `The maximum redemption amount is ${formatCurrency(String(maxDollarsPerMonth), {
          isFloat: false,
          decimals: null,
        })}.`,
      )
      return
    }

    setPointsToRedeem(points.toString())
    setCreditAmount(cashForMortgageAmount.toString())
    setConversionRate(`1 Mesa Point : $${dollarsPerPointConversion}`)
    setRemainingPoints((pointsBalance - points).toString())
    router.push({
      pathname: '/main/points-to-cash-for-mortgage/mortgage-address-selector',
    })
  }

  useEffect(() => {
    clear()
    setIdempotencyKey(randomUUID())
  }, [clear, setIdempotencyKey])

  return (
    <StandaloneWrapper
      screen="/main/points-to-cash-for-mortgage"
      title="Turn Points into Cash for Your Mortgage"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
    >
      {!loadingMetrics && (!isEligible || !isRemainingMonthlyCentsEligible) && (
        <DesignSystem.Card style={{ backgroundColor: mesaColorNames['Mesa Third #E78A43'] }}>
          <View>
            <Text>
              Currently not eligible for redemption, redemptions are limited to{' '}
              {formatCurrency(String(maxDollarsPerMonth), {
                decimals: null,
                isFloat: false,
              })}{' '}
              every {maxPeriodDays} days. You have redeemed a total of{' '}
              {formatCurrency(
                String((data?.getCashForMortgageRedemptionMetrics.totalAmountCents ?? 0) / 100),
                {
                  decimals: null,
                  isFloat: false,
                },
              )}{' '}
              for this period.
            </Text>
          </View>
        </DesignSystem.Card>
      )}
      <DesignSystem.PointsRedemption.PointConversionInfoCard
        pointsBalance={pointsBalance}
        dollarsPerPointConversion={dollarsPerPointConversion}
        conversionRatePointsExample={2_500}
      />

      <DesignSystem.PointsRedemption.SelectCreditAmount
        pointsToRedeem={points}
        creditAmount={cashForMortgageAmount}
        setCreditAmount={setCashForMortgageAmount}
      />

      <DesignSystem.Card style={{ padding: 16, borderRadius: 12 }}>
        <DesignSystem.Accordion trigger="How does this work?">
          <DesignSystem.Markdown
            style={{
              body: {
                ...typography['paragraph/sm'],
                color: mesaColorNames['Mesa - Brand/Blackish #222222'],
              },
            }}
            children={
              "**1. Redeem Your Points**\nChoose how many Mesa Points you want to convert into cash\n\n**2. Confirm Your Account**\nSelect the linked bank account where you'd like the funds sent\n\n**3. Get Rewarded**\nWe'll deposit the cash directly into your account to help cover your next mortgage payment\n\nThis feature does not facilitate direct payment to your mortgage servicer and should not be interpreted as a mortgage payment processing service. You are solely responsible for using the redeemed funds to make any mortgage payment."
            }
          />
        </DesignSystem.Accordion>
      </DesignSystem.Card>

      <Button
        onPress={handleContinue}
        disabled={
          !points ||
          !cashForMortgageAmount ||
          loadingMetrics ||
          !isEligible ||
          !isRemainingMonthlyCentsEligible
        }
        title="Continue"
        style={{ marginBottom: 40 }}
      />
    </StandaloneWrapper>
  )
}

export const PointsToCashForMortgageScreen = {
  Screen: PointsToCashForMortgage,
  Name: 'index',
}
